/** The shell scrolls main on desktop and the document on mobile. */
export function workspaceScrollParent(element: HTMLElement): HTMLElement | null {
  const win = element.ownerDocument.defaultView;
  if (!win) return null;
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    if (/^(auto|scroll|hidden)$/.test(win.getComputedStyle(parent).overflowY)) return parent;
  }
  return null;
}

/** Unlike scrollIntoView, move only the owning scroller, never all ancestors. */
export function scrollWorkspaceTarget(target: HTMLElement, margin?: number) {
  const win = target.ownerDocument.defaultView;
  if (!win) return;
  const offset = margin ?? (Number.parseFloat(win.getComputedStyle(target).scrollMarginTop) || 0);
  const container = workspaceScrollParent(target);
  const top = target.getBoundingClientRect().top;
  if (container) {
    container.scrollTo({
      top:
        container.scrollTop +
        top -
        container.getBoundingClientRect().top -
        container.clientTop -
        offset,
      behavior: "instant",
    });
  } else {
    win.scrollTo({ top: win.scrollY + top - offset, behavior: "instant" });
  }
}

/** Reserve the sticky bars belonging to this scroller, including their top inset. */
export function workspaceTargetMargin(target: HTMLElement, gap = 8) {
  const win = target.ownerDocument.defaultView;
  if (!win) return gap;
  const container = workspaceScrollParent(target);
  return Math.ceil(
    [
      ...target.ownerDocument.querySelectorAll<HTMLElement>("[data-project-anchor-occluder]"),
    ].reduce((height, bar) => {
      const style = win.getComputedStyle(bar);
      if (!/^(sticky|fixed)$/.test(style.position) || workspaceScrollParent(bar) !== container)
        return height;
      return Math.max(
        height,
        Math.max(0, Number.parseFloat(style.top) || 0) + bar.getBoundingClientRect().height,
      );
    }, 0) + gap,
  );
}

/** Confirm geometry after scrolling; a located element may still be covered. */
export function workspaceTargetVisible(target: HTMLElement) {
  const win = target.ownerDocument.defaultView;
  if (!win) return false;
  const container = workspaceScrollParent(target);
  const bounds = container?.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  const top = (bounds?.top ?? 0) + (container?.clientTop ?? 0);
  const bottom = container ? top + container.clientHeight : win.innerHeight;
  const left = bounds?.left ?? 0;
  const right = bounds?.right ?? win.innerWidth;
  const coveredTo = [
    ...target.ownerDocument.querySelectorAll<HTMLElement>("[data-project-anchor-occluder]"),
  ].reduce((edge, bar) => {
    const style = win.getComputedStyle(bar);
    const r = bar.getBoundingClientRect();
    return /^(sticky|fixed)$/.test(style.position) &&
      workspaceScrollParent(bar) === container &&
      r.right > rect.left &&
      r.left < rect.right &&
      r.top <= rect.top
      ? Math.max(edge, r.bottom)
      : edge;
  }, top);
  return (
    rect.height > 0 &&
    rect.width > 0 &&
    rect.top >= coveredTo &&
    rect.bottom <= bottom &&
    rect.left >= left &&
    rect.right <= right
  );
}
